import React, { useState } from 'react';
import { Button } from './ui/button';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Instagram,
  Linkedin,
  ArrowRight,
  Heart,
  X,
  Briefcase,
  Users,
  GraduationCap
} from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showCareersModal, setShowCareersModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_better-3ts-web/artifacts/xg6h7k8b_image.png" 
                  alt="Three Thirds Society Logo"
                  className="w-12 h-12 object-contain"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold">Three Thirds Society</h3>
                <p className="text-emerald-400 text-sm">Social Economy & Innovation</p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">
              Empowering communities and creating lasting social impact through innovative solutions, 
              comprehensive training, and sustainable development programs across Greece.
            </p>
            <div className="flex space-x-4" data-testid="social-links-container">
              <a 
                href="https://www.facebook.com/ThreeThirdsSociety/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 transition-all duration-300 hover:text-blue-500 hover:scale-110"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/3tsnpo/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 transition-all duration-300 hover:text-pink-500 hover:scale-110"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/in/three-thirds-society-npo-180b65278/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 transition-all duration-300 hover:text-blue-600 hover:scale-110"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <a href="#home" className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center group">
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  About Us
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center group">
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  Our Services
                </a>
              </li>
              <li>
                <a href="#projects" className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center group">
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  Projects
                </a>
              </li>
              <li>
                <a href="#news" className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center group">
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  News & Updates
                </a>
              </li>
              <li>
                <button 
                  onClick={() => setShowCareersModal(true)}
                  className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  Careers
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {[
                'Social Enterprise Development',
                'Training & Capacity Building',
                'Research & Innovation',
                'Project Management',
                'Advisory Services',
                'Networking & Partnerships'
              ].map((service, index) => (
                <li key={index}>
                  <a 
                    href="#services" 
                    className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Documents & Contact */}
          <div>
            <h4 className="text-lg font-bold mb-6">Company Documents</h4>
            <ul className="space-y-3 mb-6">
              <li>
                <a 
                  href="https://customer-assets.emergentagent.com/job_better-3ts-web/artifacts/hlyo8o6s_3TS_PROFILE_ENG.pdf" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  3TS Profile (PDF)
                </a>
              </li>
              <li>
                <a 
                  href="https://customer-assets-lqy194kg.emergentagent.net/job_772d08b5-e45a-4ca4-b2b2-689957d269ae/artifacts/y8qi8ayt_3TS_PIF.pdf" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-emerald-400 transition-colors duration-300 flex items-center group"
                >
                  <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  3TS PIF (PDF)
                </a>
              </li>
            </ul>
            
            {/* Contact Info */}
            <h4 className="text-lg font-bold mb-4">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-emerald-500" />
                <span className="text-gray-300 text-sm">Pellis 2, Nea Filadelfia, Attiki</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-emerald-500" />
                <span className="text-gray-300 text-sm">+30 210 2718039</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-emerald-500" />
                <span className="text-gray-300 text-sm">info@3ts.gr</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4 text-gray-400 text-sm">
              <span>© {currentYear} Three Thirds Society. All rights reserved.</span>
            </div>
            
            <div className="flex items-center space-x-1 text-gray-400 text-sm">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-red-500 mx-1" />
              <span>for social impact</span>
            </div>
            
            <div className="flex space-x-6 text-gray-400 text-sm">
              <button 
                onClick={() => setShowPrivacyModal(true)}
                className="hover:text-emerald-400 transition-colors duration-300"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setShowTermsModal(true)}
                className="hover:text-emerald-400 transition-colors duration-300"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => setShowCookieModal(true)}
                className="hover:text-emerald-400 transition-colors duration-300"
              >
                Cookie Policy
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Careers Modal */}
      {showCareersModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowCareersModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Briefcase className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">Careers at 3TS</h2>
                    <p className="text-emerald-100">Join our mission to create social impact</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCareersModal(false)}
                  className="text-white/80 hover:text-white p-1"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Why Work With Us?</h3>
                  <p className="text-gray-600">
                    At Three Thirds Society, we believe in empowering not just communities, but also our team members. 
                    Join us in making a real difference in the social economy sector across Greece and Europe.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-emerald-50 rounded-lg p-4 text-center">
                    <Users className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-900">Collaborative Team</h4>
                    <p className="text-sm text-gray-600">Work with passionate professionals</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4 text-center">
                    <GraduationCap className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-900">Growth Opportunities</h4>
                    <p className="text-sm text-gray-600">Continuous learning & development</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4 text-center">
                    <Heart className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-900">Meaningful Work</h4>
                    <p className="text-sm text-gray-600">Create real social impact</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Openings</h3>
                  <p className="text-gray-600 mb-4">
                    We're always looking for talented individuals who share our passion for social change. 
                    While we may not have specific positions listed at the moment, we welcome spontaneous applications.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-gray-700">
                      <strong>Interested in joining our team?</strong><br />
                      Send your CV and a cover letter explaining your interest in social economy to:
                    </p>
                    <a 
                      href="mailto:info@3ts.gr?subject=Career Inquiry" 
                      className="inline-flex items-center mt-2 text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      info@3ts.gr
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <a 
                href="#contact" 
                onClick={() => setShowCareersModal(false)}
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Contact Us
              </a>
              <Button
                onClick={() => setShowCareersModal(false)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Privacy Policy</h2>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-white/80 hover:text-white p-1"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 mb-4">
                  <strong>Last Updated:</strong> January 2024
                </p>
                
                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">1. Information We Collect</h3>
                <p className="text-gray-600 mb-4">
                  Three Thirds Society ("3TS", "we", "us") collects personal information that you voluntarily provide when contacting us through our website, including your name, email address, phone number, and any message content you submit through our contact form.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">2. How We Use Your Information</h3>
                <p className="text-gray-600 mb-4">
                  We use the information collected to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4">
                  <li>Respond to your inquiries and requests</li>
                  <li>Provide information about our services and projects</li>
                  <li>Send newsletters and updates (only with your consent)</li>
                  <li>Improve our website and services</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">3. Data Protection</h3>
                <p className="text-gray-600 mb-4">
                  We are committed to protecting your personal data in accordance with the General Data Protection Regulation (GDPR) and Greek data protection laws. Your data is stored securely and is not shared with third parties without your explicit consent.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">4. Your Rights</h3>
                <p className="text-gray-600 mb-4">
                  You have the right to access, correct, delete, or restrict the processing of your personal data. To exercise these rights, please contact us at info@3ts.gr.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">5. Contact</h3>
                <p className="text-gray-600">
                  For privacy-related inquiries, please contact:<br />
                  <strong>Three Thirds Society</strong><br />
                  Email: info@3ts.gr<br />
                  Address: Pellis 2, Nea Filadelfia, Attiki, Greece
                </p>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <Button
                onClick={() => setShowPrivacyModal(false)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowTermsModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Terms of Service</h2>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="text-white/80 hover:text-white p-1"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 mb-4">
                  <strong>Last Updated:</strong> January 2024
                </p>
                
                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">1. About Three Thirds Society</h3>
                <p className="text-gray-600 mb-4">
                  Three Thirds Society (3TS) is a non-profit organization dedicated to promoting social economy, innovation, and sustainable development in Greece and across Europe. By accessing our website, you agree to these terms.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">2. Use of Website</h3>
                <p className="text-gray-600 mb-4">
                  This website is provided for informational purposes about our organization, services, and projects. You may browse, download, and print content for personal, non-commercial use only. Any other use requires our written permission.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">3. Intellectual Property</h3>
                <p className="text-gray-600 mb-4">
                  All content on this website, including text, images, logos, and documents, is the property of Three Thirds Society or its partners and is protected by copyright and intellectual property laws. Project logos and materials may be subject to EU funding requirements and attribution.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">4. Disclaimer</h3>
                <p className="text-gray-600 mb-4">
                  While we strive to keep information accurate and up-to-date, we make no warranties about the completeness or accuracy of the content. Information about EU-funded projects is provided for transparency and may be subject to change.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">5. Limitation of Liability</h3>
                <p className="text-gray-600 mb-4">
                  Three Thirds Society shall not be liable for any direct, indirect, or consequential damages arising from the use of this website or reliance on any information provided.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">6. Governing Law</h3>
                <p className="text-gray-600">
                  These terms are governed by the laws of the Hellenic Republic (Greece). Any disputes shall be subject to the exclusive jurisdiction of the courts of Athens, Greece.
                </p>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <Button
                onClick={() => setShowTermsModal(false)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Policy Modal */}
      {showCookieModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowCookieModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Cookie Policy</h2>
                <button
                  onClick={() => setShowCookieModal(false)}
                  className="text-white/80 hover:text-white p-1"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 mb-4">
                  <strong>Last Updated:</strong> January 2024
                </p>
                
                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">1. What Are Cookies?</h3>
                <p className="text-gray-600 mb-4">
                  Cookies are small text files stored on your device when you visit a website. They help improve your browsing experience and allow us to understand how visitors use our site.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">2. Cookies We Use</h3>
                <p className="text-gray-600 mb-2">
                  Our website uses minimal cookies:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4">
                  <li><strong>Essential Cookies:</strong> Required for basic website functionality</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our site (anonymous data only)</li>
                  <li><strong>Social Media Cookies:</strong> May be set when you interact with our Facebook integration</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">3. Third-Party Cookies</h3>
                <p className="text-gray-600 mb-4">
                  Our website may include content from third parties (such as Facebook) that may set their own cookies. We have no control over these cookies. Please refer to the respective third-party privacy policies for more information.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">4. Managing Cookies</h3>
                <p className="text-gray-600 mb-4">
                  You can control and delete cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our website. Most browsers allow you to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4">
                  <li>View cookies stored on your device</li>
                  <li>Delete all or specific cookies</li>
                  <li>Block cookies from all or specific websites</li>
                  <li>Set preferences for cookie acceptance</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">5. Contact Us</h3>
                <p className="text-gray-600">
                  If you have questions about our cookie policy, please contact us at info@3ts.gr.
                </p>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <Button
                onClick={() => setShowCookieModal(false)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};
